import {memo} from 'react';
import {MUIBox, MUITypography} from '@/components/MUI-Components';
import Eyebrow from '@/components/Common/Eyebrow';
import {csx} from './caseStudyStyles';

interface SectionHeaderProps {
    eyebrow?: string;
    heading: string;
    subheading?: string;
    /** Section sits on the dark theme background — switches the eyebrow to its on-dark color. */
    onDark?: boolean;
    wideHeading?: boolean;
    /** `p` for CTA blocks, whose heading is promotional copy rather than part of the page outline. */
    headingTag?: 'h2' | 'p';
}

/**
 * The one genuinely-shared piece across sections: the centered
 * eyebrow + heading + subheading block. Ten sections render this identical
 * structure, and it encodes the site-wide header rule (centered, max-width,
 * mb gap), so it should change in exactly one place. An <h2> by default — the hero
 * owns the single <h1> — or a <p> for CTA blocks.
 */
const SectionHeader = ({eyebrow, heading, subheading, onDark, wideHeading, headingTag = 'h2'}: SectionHeaderProps) => (
    <MUIBox sx={[csx.headerBlock, wideHeading ? {maxWidth: 'none'} : {}]}>
        {eyebrow ? <Eyebrow text={eyebrow} onDark={onDark} /> : null}
        <MUITypography
            component={headingTag}
            className="font-headers"
            sx={[csx.heading, {paddingInline: '16px'}, onDark ? {color: 'var(--cs-on-dark)'} : {}]}
        >
            {heading}
        </MUITypography>
        {subheading ? (
            <MUITypography className="font-regular-text" sx={[csx.subheading, onDark ? {color: 'var(--cs-on-dark)'} : {}]}>
                {subheading}
            </MUITypography>
        ) : null}
    </MUIBox>
);

export default memo(SectionHeader);
